/** What an issue links to (PT-7117): a documentation (always its current version) or one of its versions. */
export enum LinkedDocumentationType {
  documentation = 'documentation',
  version = 'version',
}

export function isDocumentationLinkType(type: LinkedDocumentationType): boolean {
  return type === LinkedDocumentationType.documentation;
}

const LINKED_DOCUMENTATION_TYPES: ReadonlySet<string> = new Set(Object.values(LinkedDocumentationType));

/** Whether a value read from the UI (e.g. a radio button) is a link type. */
export function isLinkedDocumentationType(value: string): value is LinkedDocumentationType {
  return LINKED_DOCUMENTATION_TYPES.has(value);
}

/** What a link points at (PT-7117): a documentation, shown in its current version, or one of its versions. */
export type LinkedDocumentationTarget =
  | { type: LinkedDocumentationType.documentation; documentationId: string }
  | { type: LinkedDocumentationType.version; documentationId: string; versionId: string };

export function isVersionLinkTarget(
  target: LinkedDocumentationTarget
): target is Extract<LinkedDocumentationTarget, { type: LinkedDocumentationType.version }> {
  return target.type === LinkedDocumentationType.version;
}

/** The version a link stores: its own for a link to a version, none (`null`) for a link to the documentation. */
export function getTargetVersionId(target: LinkedDocumentationTarget): string | null {
  return isVersionLinkTarget(target) ? target.versionId : null;
}

/** What a link shows, as its tile tells it (PT-7117). */
export enum LinkedDocumentationDescriptionKind {
  /** A link to the documentation, shown in its current version. */
  currentVersionOfDocumentation = 'currentVersionOfDocumentation',
  /** A link to the documentation, which has no current version to show. */
  documentationWithoutCurrentVersion = 'documentationWithoutCurrentVersion',
  /** A link to one version, whatever its documentation's current one is. */
  version = 'version',
}

export type LinkedDocumentationItem = {
  name: string;
  description?: string;
  _id?: string;
  localId?: number;
  /** Missing on links stored before PT-7117: they point at a version. */
  type?: LinkedDocumentationType;
  documentationId?: string;
  /** `null` for a link to the documentation. */
  versionId?: string | null;
  imageRotation?: number;
  sourceDocumentId?: string;
  createdAt?: string;
  createdBy?: string;
  documentationName?: string;
  label?: string;
  deleted: boolean;
};

export type LinkedDocumentationOnView<TFile = unknown> = {
  id: string;
  title: string;
  file?: TFile;
};

export type LinkedDocPreview = {
  url: string;
  imageSignedRequest?: string;
  thumbnailSignedRequest?: string;
  mergedSignedRequest?: string;
  mergedThumbnailSignedRequest?: string;
  drawingSignedRequest?: string;
  imageRotation?: number;
  viaServiceWorker?: boolean;
};

export type RequestableLinkedDocumentationItem = LinkedDocumentationItem & {
  documentationId: string;
  versionId: string;
};

export enum ResourceLoadStatus {
  Idle = 'idle',
  Loading = 'loading',
  Ready = 'ready',
  Error = 'error',
}

export type LinkedDocumentationResourceMeta = {
  status: ResourceLoadStatus;
  requestedAt?: number;
  fetchedAt?: number;
  expiresAt?: number;
  nextRetryAt?: number;
  retryCount: number;
  lastError?: string;
};

export type MaybeNumber = number | null | undefined;

export type LinkedDescriptionMap = Record<string, string>;

export type CreateLinkedDocumentationItemParams = {
  type: LinkedDocumentationType;
  documentationId: string;
  /** `null` for a link to the documentation. */
  versionId: string | null;
  sourceDocumentId?: string;
  nameCandidates: Array<string | undefined | null>;
  descriptionCandidates?: Array<string | undefined | null>;
  imageRotationCandidates?: MaybeNumber[];
};

export type LinkedDocumentationReference = {
  linkedDocumentationId?: string;
  documentationId?: string;
  versionId?: string;
};
